<template>
  <DemoModal :show="isModalOpen" :url="brevoLink" @close="closeModal" />
  <!-- 1. SECCIÓN VERTICAL INICIAL (TU CONTENIDO HERO) -->
  <section class="hero-section">
    <div class="hero-container">
      <div class="hero-content">
        <div class="hero-text">
          <!-- El badge y el botón tendrán una animación más simple -->
          <div class="hero-badge" ref="badgeRef">
            <span class="badge-text">Nuevo</span>
            <span class="badge-description"
              >Software CMMS en la nube con dispositivos inteligentes y geolocalización 🎉</span
            >
            <svg class="badge-arrow" width="16" height="16" viewBox="0 0 24 24" fill="none">
              <path
                d="M7 17L17 7M17 7H7M17 7V17"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </div>

          <!-- TÍTULO ENVUELTO EN SU MÁSCARA -->
          <div class="text-mask">
            <h1 class="hero-title" ref="titleRef">
              Gestión de activos para<br />
              mantenimiento preventivo<br />
              y predictivo
            </h1>
          </div>

          <!-- DESCRIPCIÓN ENVUELTA EN SU MÁSCARA -->
          <div class="text-mask">
            <p class="hero-description" ref="descriptionRef">
              Reduce paradas, costos y riesgos con un CMMS fácil de usar, 100% en la nube y con integración nativa a sensores inteligentes y geolocalización.
            </p>
          </div>

          <button class="hero-cta" ref="ctaRef" @click="openModal">
            <span>Solicita una Demo</span>
            <svg class="cta-arrow" width="20" height="20" viewBox="0 0 24 24" fill="none">
              <path
                d="M7 17L17 7M17 7H7M17 7V17"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </button>
        </div>
      </div>
    </div>
  </section>

  <section ref="horizontalSectionRef" class="horizontal-section">
    <div ref="trackRef" class="horizontal-track">
      <div class="panel">
        <img
          src="/src/assets/images/banner.png"
          alt="Dashboard de gestión de activos FTrack"
          class="dashboard-image"
        />
      </div>

      <div class="panel">
        <img
          src="/src/assets/images/banner3.png"
          alt="Dashboard de gestión de activos FTrack - operarios"
          class="dashboard-image"
        />
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import { onMounted, ref } from 'vue'
import { gsap } from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import DemoModal from './DemoModal.vue'
import { useDemoModal } from '../composables/useDemoModal'

gsap.registerPlugin(ScrollTrigger)

const { isModalOpen, brevoLink, openModal, closeModal } = useDemoModal()

const horizontalSectionRef = ref<HTMLElement | null>(null)
const trackRef = ref<HTMLElement | null>(null)

const badgeRef = ref<HTMLElement | null>(null)
const titleRef = ref<HTMLElement | null>(null)
const descriptionRef = ref<HTMLElement | null>(null)
const ctaRef = ref<HTMLElement | null>(null)

onMounted(() => {
  const heroElements = [
    badgeRef.value,
    titleRef.value,
    descriptionRef.value,
    ctaRef.value
  ];
  gsap.set(heroElements, { autoAlpha: 0 });

  const heroTl = gsap.timeline({
    defaults: { duration: 0.8, ease: 'power3.out' },
    delay: 0.2
  });

  gsap.set(badgeRef.value, { autoAlpha: 0, y: 20 });
  gsap.set(titleRef.value, { autoAlpha: 0, yPercent: 100 });
  gsap.set(descriptionRef.value, { autoAlpha: 0, yPercent: 100 });
  gsap.set(ctaRef.value, { autoAlpha: 0, yPercent: 100 });

  heroTl
    .to(badgeRef.value, { autoAlpha: 1, y: 0 })
    .to(titleRef.value, { autoAlpha: 1, yPercent: 0 }, '-=0.7')
    .to(descriptionRef.value, { autoAlpha: 1, yPercent: 0 }, '-=0.7')
    .to(ctaRef.value, { autoAlpha: 1, yPercent: 0 }, '-=0.7');

})
</script>

<style scoped>
.hero-section {
  background: #fff;
  padding-top: 90px;
  display: flex;
  align-items: center;
  flex-direction: column;
}

.hero-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
  width: 100%;
  position: relative;
  z-index: 1;
}

.hero-content {
  display: flex;
  gap: 60px;
  flex-direction: column;
}

.hero-text {
  display: flex;
  flex-direction: column;
  gap: 32px;
}

.hero-badge {
  display: inline-flex;
  align-items: center;
  gap: 12px;
  background: white;
  border-radius: 50px;
  width: fit-content;
  transition: all 0.3s ease;
}

.hero-badge:hover {
  transform: translateY(-2px);
}

.badge-text {
  background: #129bef;
  color: white;
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.badge-description {
  color: #64748b;
  font-size: 18px;
  font-weight: 500;
}

.badge-arrow {
  color: #129bef;
  transition: transform 0.3s ease;
}

.hero-badge:hover .badge-arrow {
  transform: translate(2px, -2px);
}

.hero-title {
  font-size: clamp(2.5rem, 4vw, 3.5rem);
  font-weight: 700;
  line-height: 1.1;
  color: #1d1e20de;
  margin: 0;
  letter-spacing: -0.02em;
}

.hero-description {
  font-size: 18px;
  line-height: 1.6;
  color: #64748b;
  margin: 0;
  max-width: 90%;
}

.hero-cta {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: #129bef;
  color: white;
  border: none;
  padding: 16px 32px;
  border-radius: 50px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  width: fit-content;
}

.hero-cta:hover {
  background: #0ea5e9;
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(18, 155, 239, 0.4);
}

.cta-arrow {
  transition: transform 0.3s ease;
}

.hero-cta:hover .cta-arrow {
  transform: translate(2px, -2px);
}

.hero-dashboard {
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
}

.dashboard-container {
  position: relative;
  width: 100%;
  max-width: 1400px;
  overflow: hidden;
  background: white;
  transition: transform 0.3s ease;
}

.horizontal-scroll-container {
  position: relative;
  width: 100%;
  height: 100vh;
  overflow: hidden;
  background: white;
}

.horizontal-scroll-container .dashboard-container {
  position: absolute;
  top: 50%;
  left: 0;
  transform: translateY(-50%);
  width: 100%;
  max-width: 1400px;
  margin: 0 auto;
}

.dashboard-image {
  width: 100%;
  height: auto;
  display: block;
}

.text-mask {
  overflow: hidden;
}

.hero-badge,
.hero-title,
.hero-description,
.hero-cta {
  visibility: hidden;
}

/* 2. SECCIÓN HORIZONTAL (EL NUEVO CONTENEDOR CLAVE) */

.horizontal-section,
.horizontal-track,
.panel {
  height: calc(100dvh + 20px);
}

.horizontal-section {
  overflow: hidden;
  position: relative;
}

.horizontal-track {
  display: flex;
  width: 200vw;
}

.panel {
  background-color: #fff;
  width: 100vw;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 20px;
  box-sizing: border-box;
}

.dashboard-image {
  width: 100%;
  max-width: 1400px;
  height: auto;
  object-fit: contain;
}

.final-section {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  background-color: #f0f4f8;
  font-size: 2rem;
}

@media (max-width: 1024px) {
  .hero-content {
    gap: 40px;
  }

  .hero-title {
    font-size: clamp(2rem, 3.5vw, 3rem);
  }
}

@media (max-width: 768px) {
  .hero-section {
    padding-top: 20px;
    min-height: auto;
  }

  .hero-content {
    grid-template-columns: 1fr;
    gap: 40px;
  }

  .hero-text {
    order: 1;
    gap: 24px;
  }

  .hero-dashboard {
    order: 2;
  }

  .hero-description {
    max-width: 100%;
  }

  .dashboard-container {
    max-width: 90%;
  }
}

@media (max-width: 480px) {
  .hero-container {
    padding: 0 15px;
  }
.hero-description {
  font-size: 15px;
}

  .hero-badge {
    display: inline-flex;
    align-items: center;
    gap: 12px;
    background: white;
    border-radius: 50px;
    width: fit-content;
    transition: all 0.3s ease;
  }

  .hero-badge svg {
    display: none;
  }
  .badge-arrow {
    display: none;

  }

  .badge-description {
    font-size: 12px;
  }

  .hero-cta {
    padding: 14px 28px;
    font-size: 14px;
  }
}

@media (max-width: 768px) {
  .horizontal-section {
    height: 50vh;
  }

  .horizontal-track,
  .panel {
    height: 50vh;
  }

  .dashboard-image {
    object-fit: contain;
    max-height: 90%;
  }

  .final-section h2 {
    font-size: 1.5rem;
    padding: 0 20px;
  }
}
</style>
